"use client";

import { useEffect, useMemo, useRef, useState } from "react";

const TABS = [
  { label: "اتاق‌ها", href: "#rooms" },
  { label: "امکانات", href: "#amenities" },
  { label: "درباره هتل", href: "#about" },
  { label: "موقعیت", href: "#location" },
  { label: "نظرات", href: "#reviews" },
  // TODO: Restore after the similar hotels section is connected to real data.
  // { label: "مشابه‌ها", href: "#similar-hotels" },
  { label: "قوانین", href: "#rules" },
  { label: "سوالات", href: "#faq" },
];

interface DetailAnchorTabsProps {
  className?: string;
}

export default function DetailAnchorTabs({
  className = "",
}: DetailAnchorTabsProps) {
  const navRef = useRef<HTMLElement | null>(null);
  const tabRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const sectionIds = useMemo(
    () => TABS.map((tab) => tab.href.replace("#", "")),
    [],
  );
  const [activeHref, setActiveHref] = useState(TABS[0].href);

  useEffect(() => {
    let ticking = false;

    const updateActiveTab = () => {
      ticking = false;

      const activationLine = window.innerHeight * 0.32;
      const activeSection = sectionIds.reduce<{
        id: string;
        distance: number;
      } | null>((closest, id) => {
          const section = document.getElementById(id);
          if (!section) return closest;

          const distance = activationLine - section.getBoundingClientRect().top;
          if (distance < 0) return closest;
          if (!closest || distance < closest.distance) return { id, distance };

          return closest;
        }, null);

      setActiveHref(`#${activeSection?.id ?? sectionIds[0]}`);
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateActiveTab);
    };

    updateActiveTab();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);

    return () => {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
    };
  }, [sectionIds]);

  useEffect(() => {
    const activeIndex = TABS.findIndex((tab) => tab.href === activeHref);
    if (activeIndex < 0) return;

    const nav = navRef.current;
    const tab = tabRefs.current[activeIndex];
    if (!nav || !tab) return;

    const navRect = nav.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const nextScrollLeft =
      nav.scrollLeft +
      tabRect.left -
      navRect.left -
      (navRect.width - tabRect.width) / 2;

    nav.scrollTo({
      left: nextScrollLeft,
      behavior: "smooth",
    });
  }, [activeHref]);

  return (
    <nav
      ref={navRef}
      aria-label="بخش‌های صفحه"
      className={`flex w-full items-center overflow-x-auto ${className}`}
    >
      {TABS.map((tab, index) => (
        <a
          ref={(element) => {
            tabRefs.current[index] = element;
          }}
          key={tab.href}
          href={tab.href}
          className={`flex h-full shrink-0 items-center border-b-2 px-3 text-[13px] font-medium transition ${
            activeHref === tab.href
              ? "border-primary text-primary"
              : "border-transparent text-[#6b7280] hover:text-primary"
          }`}
        >
          {tab.label}
        </a>
      ))}
    </nav>
  );
}
